import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import {
  evidenceSubmissionsApi, documentsApi, type Deliverable, type EvidenceFormat, type LinkAttachment,
} from '../../services/api';
import type { Person } from '../../types/models';
import { reactSelectStyles } from '../../utils/reactSelectStyles';
import { useToast } from '../../hooks/useToast';
import { computeTeamHealth, type TeamHealthStatus, type CheckResult } from '../../utils/evidenceHealth';
import { AttachmentSection, type PendingFileAttachment } from './AttachmentSection';

interface SubmissionLite {
  submissionId: string;
  submittedAt: string;
  status: 'Submitted' | 'In Review' | 'Accepted' | 'Rejected' | 'Clarification Needed';
  teamDepartmentId?: string;
  deliverables?: Deliverable[];
  owner?: string | null;
  scopeDescription?: string;
  evidenceFormat?: EvidenceFormat;
  linkAttachments?: LinkAttachment[];
  notes?: string;
  locked?: boolean;
  reviewPeriodStart?: string | null;
  reviewPeriodEnd?: string | null;
}

interface TeamEntry { team: string; teamDepartmentId?: string; latest: SubmissionLite | undefined }

const HEALTH_TONE: Record<TeamHealthStatus, string> = {
  Healthy: 'success', 'Needs attention': 'warning', Failing: 'danger', Pending: 'neutral',
};

const avatarColorFor = (team: string): string => {
  let hash = 0;
  for (let i = 0; i < team.length; i++) hash = team.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${hash % 360}, 55%, 55%)`;
};
const initials = (team: string): string => team.trim().slice(0, 2).toUpperCase();

/** Attachments actually seen by the health check: already-uploaded documents (read back
 * with their own capturedAt) plus staged-but-not-yet-uploaded files/links in the composer. */
function useLiveAttachments(
  existingDocCapturedAts: string[], pendingFiles: PendingFileAttachment[], linkAttachments: LinkAttachment[],
) {
  return useMemo(() => [
    ...existingDocCapturedAts.map((c, i) => ({ id: `doc-${i}`, capturedAt: c })),
    ...pendingFiles.map((p) => ({ id: p.file.name, capturedAt: p.capturedAt })),
    ...linkAttachments.map((l) => ({ id: l.id, capturedAt: l.capturedAt })),
  ], [existingDocCapturedAts, pendingFiles, linkAttachments]);
}

const CHECK_ICON: Record<CheckResult['status'], string> = {
  pass: 'ri-checkbox-circle-fill', warning: 'ri-alert-fill', fail: 'ri-close-circle-fill', pending: 'ri-time-line',
};

const HealthChecklist: React.FC<{ checks: CheckResult[] }> = ({ checks }) => (
  <div className="d-flex flex-column gap-1">
    {checks.map((c) => (
      <div key={c.id} className="d-flex align-items-start gap-2">
        <i className={`${CHECK_ICON[c.status]} np-tone-${HEALTH_TONE[c.status === 'pass' ? 'Healthy' : c.status === 'fail' ? 'Failing' : c.status === 'pending' ? 'Pending' : 'Needs attention']}-text`}></i>
        <div>
          <div className="fw-medium" style={{ fontSize: '0.8rem' }}>{c.label}</div>
          <div className="np-sub" style={{ fontSize: '0.75rem' }}>{c.reason}</div>
        </div>
      </div>
    ))}
  </div>
);

export const TeamSubmissionPanel: React.FC<{
  evidenceId: string;
  teamData: TeamEntry[];
  /** Parent evidence's requirements template, for a team's first round. */
  requirements: Deliverable[];
  gracePeriodDays: number;
  dueDate: string | undefined;
  people: Person[];
  pickedTeam: string | null;
  onPickTeam: (team: string) => void;
}> = ({ evidenceId, teamData, requirements, gracePeriodDays, dueDate, people, pickedTeam, onPickTeam }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const now = useMemo(() => new Date(), []);

  const peopleOptions = useMemo(() => people.map((p) => ({ value: p.personId, label: p.personName })), [people]);

  const current = teamData.find((t) => t.team === pickedTeam) || teamData[0];
  const latest = current?.latest;
  const isLocked = !!latest?.locked;

  // Composer state - initialised from the latest round when it's been reopened (not
  // locked), otherwise blank defaults for a fresh submission.
  const [forceNew, setForceNew] = useState(false);
  const composing = !latest || !isLocked || forceNew;
  const seed = !forceNew && latest ? latest : undefined;

  const [owner, setOwner] = useState(seed?.owner || '');
  const [scopeDescription, setScopeDescription] = useState(seed?.scopeDescription || '');
  const [evidenceFormat, setEvidenceFormat] = useState<EvidenceFormat>(seed?.evidenceFormat || 'config_export');
  const [notes, setNotes] = useState(seed?.notes || '');
  const [pendingFiles, setPendingFiles] = useState<PendingFileAttachment[]>([]);
  const [linkAttachments, setLinkAttachments] = useState<LinkAttachment[]>(seed?.linkAttachments || []);
  const [checklist, setChecklist] = useState<Deliverable[]>(
    () => (seed?.deliverables && seed.deliverables.length > 0 ? seed.deliverables : requirements).map((d) => ({ ...d })),
  );

  const toggleRequirement = (id: string) =>
    setChecklist((prev) => prev.map((d) => (d.id === id ? { ...d, status: d.status === 'Done' ? 'Pending' : 'Done' } : d)));

  const liveAttachments = useLiveAttachments([], pendingFiles, linkAttachments);

  const liveHealth = useMemo(() => computeTeamHealth({
    dueDate: dueDate || now.toISOString().slice(0, 10),
    gracePeriodDays,
    reviewPeriodStart: latest?.reviewPeriodStart || dueDate || now.toISOString().slice(0, 10),
    reviewPeriodEnd: latest?.reviewPeriodEnd || dueDate || now.toISOString().slice(0, 10),
    submittedAt: composing ? null : latest!.submittedAt,
    requirements: checklist.map((d) => ({ id: d.id, done: d.status === 'Done' })),
    attachments: liveAttachments,
    now,
  }), [dueDate, gracePeriodDays, latest, composing, checklist, liveAttachments, now]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const created = await evidenceSubmissionsApi.create(evidenceId, {
        notes, teamName: current.team, teamDepartmentId: current.teamDepartmentId,
        deliverables: checklist, owner: owner || undefined, scopeDescription, evidenceFormat, linkAttachments,
      });
      const submissionId: string = created?.submission?.submissionId;
      if (submissionId) {
        for (const p of pendingFiles) {
          await documentsApi.upload('evidence', submissionId, p.file, undefined, new Date(p.capturedAt).toISOString());
        }
      }
      return created;
    },
    onSuccess: () => {
      toast.success('Submission recorded.');
      queryClient.invalidateQueries({ queryKey: ['evidenceSubmissions', evidenceId] });
      setForceNew(false);
      setPendingFiles([]);
    },
    onError: (e: any) => toast.error(e.message || 'Failed to submit.'),
  });

  const reopenMutation = useMutation({
    mutationFn: () => evidenceSubmissionsApi.reopen(evidenceId, latest!.submissionId),
    onSuccess: () => {
      toast.success('Submission reopened for editing.');
      queryClient.invalidateQueries({ queryKey: ['evidenceSubmissions', evidenceId] });
    },
    onError: (e: any) => toast.error(e.message || 'Only this team\'s owner, a delegate, or an admin may reopen it.'),
  });

  if (teamData.length === 0) {
    return (
      <div className="text-center np-sub py-4">
        <i className="ri-group-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
        No teams yet. Add one to start collecting evidence for this requirement.
      </div>
    );
  }

  return (
    <div>
      {/* Team switcher - each badge is this team's own computed health, not the reviewer
          decision (a team can be "Healthy" on the 4 checks while still Awaiting review). */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        {teamData.map((t) => {
          const health = computeTeamHealth({
            dueDate: dueDate || now.toISOString().slice(0, 10),
            gracePeriodDays,
            reviewPeriodStart: t.latest?.reviewPeriodStart || dueDate || now.toISOString().slice(0, 10),
            reviewPeriodEnd: t.latest?.reviewPeriodEnd || dueDate || now.toISOString().slice(0, 10),
            submittedAt: t.latest?.submittedAt || null,
            requirements: (t.latest?.deliverables || requirements).map((d) => ({ id: d.id, done: d.status === 'Done' })),
            attachments: [],
            now,
          });
          return (
            <button
              key={t.team} type="button"
              className={`np-team-pick${t.team === current.team ? ' active' : ''}`}
              onClick={() => onPickTeam(t.team)}
            >
              <span className="np-matrix-avatar" style={{ background: avatarColorFor(t.team) }}>{initials(t.team)}</span>
              {t.team}
              <span className={`np-chip np-tone-${HEALTH_TONE[health.status]} ms-1`}>{health.status}</span>
            </button>
          );
        })}
      </div>

      <div className="np-card">
        <div className="np-card-head d-flex justify-content-between align-items-center">
          <h2 className="mb-0">{current.team}</h2>
          <span className={`np-chip np-tone-${HEALTH_TONE[liveHealth.status]}`}>{liveHealth.status}</span>
        </div>
        <div className="np-card-body">
          {!composing ? (
            // Locked, already-submitted round - read-only summary + actions.
            <div>
              <div className="np-row2 mb-2">
                <div><span className="np-sub">Owner</span><div>{peopleOptions.find((o) => o.value === latest!.owner)?.label || '—'}</div></div>
                <div><span className="np-sub">Scope</span><div>{latest!.scopeDescription || '—'}</div></div>
              </div>
              <p className="np-sub mb-3">{latest!.notes || 'No notes.'}</p>
              <HealthChecklist checks={liveHealth.checks} />
              <div className="mt-3 d-flex gap-2">
                <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => reopenMutation.mutate()} disabled={reopenMutation.isPending}>
                  <i className="ri-lock-unlock-line"></i>Reopen
                </button>
                <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setForceNew(true)}>
                  <i className="ri-add-line"></i>Start a new submission
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="np-row2">
                <div className="np-field">
                  <label>Owner</label>
                  <Select options={peopleOptions} value={peopleOptions.find((o) => o.value === owner) || null}
                    onChange={(opt) => setOwner(opt?.value || '')} isClearable styles={reactSelectStyles()} menuPortalTarget={document.body} />
                </div>
                <div className="np-field">
                  <label>Evidence format</label>
                  <select className="np-select" value={evidenceFormat} onChange={(e) => setEvidenceFormat(e.target.value as EvidenceFormat)}>
                    <option value="config_export">Config export</option>
                    <option value="screenshots">Screenshots</option>
                    <option value="ticket_links">Ticket links</option>
                  </select>
                </div>
              </div>
              <div className="np-field">
                <label>Scope description</label>
                <input className="np-input" value={scopeDescription} onChange={(e) => setScopeDescription(e.target.value)} placeholder="What this submission covers…" />
              </div>

              <div className="np-field">
                <label>Requirements</label>
                {checklist.length === 0 ? (
                  <div className="np-sub small">No requirements assigned to this team.</div>
                ) : checklist.map((d) => (
                  <label key={d.id} className="d-flex align-items-center gap-2 mb-1">
                    <input type="checkbox" checked={d.status === 'Done'} onChange={() => toggleRequirement(d.id)} />
                    <span>{d.description}</span>
                  </label>
                ))}
              </div>

              <div className="np-field">
                <label>Attachments</label>
                <AttachmentSection
                  evidenceFormat={evidenceFormat}
                  pendingFiles={pendingFiles} onPendingFilesChange={setPendingFiles}
                  linkAttachments={linkAttachments} onLinkAttachmentsChange={setLinkAttachments}
                  disabled={submitMutation.isPending}
                />
              </div>

              <div className="np-field">
                <label>Notes for the reviewer</label>
                <textarea className="np-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              <div className="np-banner np-tone-neutral mb-3">
                <HealthChecklist checks={liveHealth.checks} />
              </div>

              <div className="d-flex gap-2">
                <button className="np-btn np-btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>
                  {submitMutation.isPending
                    ? <span className="spinner-border spinner-border-sm"></span>
                    : <><i className="ri-send-plane-line"></i>Submit</>}
                </button>
                {latest && isLocked === false && (
                  <button className="np-btn np-btn-ghost" onClick={() => setForceNew(false)} disabled={submitMutation.isPending}>Cancel</button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
